import { Injectable } from '@angular/core';
import { Pako } from "./pako.service";
import { HTTPService } from "./http.service";
import { DacConfig } from "./dac-config.service";
import { UniqueIdService } from "./unique-id.service";
import { AssessmentResultHydrator } from "./assessment-result-hydrator.service";
import { AssessmentHydrator } from "./assessment-hydrator.service";

import {Assessment} from "../models/assessment"
import {AssessmentResult} from "../models/assessment-result"

import {AssessmentCompressorService} from "./assessment-compressor.service";
import { AssignedAssessment } from '../models/AssignedAssessment';

@Injectable()
export class AssessmentService
{
	private API_URL;
	private _results:Map<String,AssessmentResult>;
	private _assessmentUid:string;
	private _clientId:string;
	private _clientCustomField1:string;
	private _assessments:Map<String,Assessment>;
	private _pendingResults:Map<String, Promise<any>>;
	
	/**
	 * Flag used for administrators to force us to allow a test to be retaken.
	*/
	private _forceTestRetake:boolean;
	
	constructor(
      // TODO: stephen replace $http here with our dac$http
      private _dac$http:HTTPService,
      private _pako:Pako,
      private _resultHydrator:AssessmentResultHydrator,
      private _hydrator:AssessmentHydrator,
	  private _compressor:AssessmentCompressorService,
      private _uuid:UniqueIdService,
      private dacConfig:DacConfig
		) {
		this.API_URL = this.dacConfig.api_url;
		this._results = new Map();
		this._assessments = new Map();
		this._pendingResults = new Map();
	}

	// TODO: stephen need to handle the caching results.
	public clearCache() {
		this._results = new Map();
		this._assessments = new Map();
		this._pendingResults = new Map();
	}
	
	public get clientId() {
		return this._clientId;
	}
	
	public set clientId(v : string) {
		this._clientId = v;
	}
	
	public get clientCustomField1() {
		return this._clientCustomField1;
	}
	
	public set clientCustomField1(v:string) {
		this._clientCustomField1 = v;
	}
	
	public get uid() : string {
		return this._assessmentUid;
	}
	
	public set uid(v : string) {
		this._assessmentUid = v;
	}
	
	public get forceTestRetake() : boolean {
		return this._forceTestRetake;
	}
	
	public set forceTestRetake(v : boolean) {
		this._forceTestRetake = v;
	}
	
	
	public newId() {
		return this._uuid.generate();
	}
	
	private createAssessmentFromJSON(data:any) : Assessment {
		return this._hydrator.hydrate(data);
	}
	
	private createAssessmentResultFromJSON(data:any) : AssessmentResult {
		return this._resultHydrator.hydrate(data);
	}
	
	hasClientTakenAssessment() {
		return new Promise((resolve, reject) => {
			if (this.forceTestRetake) {
				resolve();
			}
			this.getLatestClientAssessmentResult(this.clientId, this._assessmentUid)
				.then((result:AssessmentResult) => {
					resolve(result.id);
				}, (error) => {
					if (error && error.status === 404) {
						resolve();
					}
					else {
						console.error("could not retrieve assessment results", error);
						reject("could not retrieve assessment results");
					}
				});
		});
	}

	getAssessmentForAssignment(assignment:AssignedAssessment) {
		var requestData = {
			clientID: this.clientId
			,assignmentItemID: assignment.Id
		};
		// TODO: stephen is this going to cause problems here??
		return this._dac$http.get("assessments/" + assignment.Uid + ".json", requestData)
		.then((response) => {
			let json = response.json() as any;
			var assessment = this.createAssessmentFromJSON(json);
			this._assessments[assignment.Uid] = assessment;
			return assessment;
		})
	}

	getAssessmentVersion(uid:string, version:number) :Promise<Assessment>{
		var requestData = {
			clientID: this.clientId
			,version: version
		};
		return this._dac$http.get("assessments/" + uid + ".json", requestData)
		.then((response) => {
			let json = response.json() as any;
			var assessment = this.createAssessmentFromJSON(json);
			return assessment;
		}, (error) => {
			console.error(error);
			throw Error("could not retrieve assessment");
		});
	}
	
	getAssessment(uid) : Promise<Assessment> {
		var that = this;
		if (this._assessments[uid]) {
			return Promise.resolve(this._assessments[uid]);
		}

		var requestData = {
			clientID: this.clientId
		};
		return this._dac$http.get("assessments/" + uid + ".json", requestData)
		.then((response) => {
			let json = response.json() as any;
			var assessment = that.createAssessmentFromJSON(json);
			this._assessments[uid] = assessment;
			return assessment;
		}, (error) => {
			console.error(error);
			throw Error("could not retrieve assessment");
		});
	}
	
	getAssessmentList() : Promise<Assessment[]> {
		return this._dac$http.get("assessments")
			.then(response => {
				let data = response.json() as Assessment[];
				if (data) {
					let assessments = data.map(i => this.createAssessmentFromJSON(i))
					.filter(a => this.dacConfig.isAssessmentEnabled(a.uid));
					return assessments;
				}
				throw new Error("No assessment data was returned");
			});
	}

	// TODO: stephen this compressed post is a leaky abstraction and doesn't work like the others
	// we need to fix it.
	createAssessment(assessment:Assessment) {
		let compressedAssessment = this._compressor.compress(assessment);
		return this._dac$http.compressedPost("assessments", compressedAssessment)
		.catch((error) => {
			console.error(error);
			throw error.response;
		});
	}
	
	saveAssessment(assessment:Assessment) {
		let compressedAssessment = this._compressor.compress(assessment);
		return this._dac$http.compressedPost("assessments/"+ assessment.uid + ".json", compressedAssessment)
		.then((result) => {
			return result;
		})
		.catch((error) => {
			console.error(error);
			throw error.response;
		});
	}
	
	saveAssessmentResult(result:AssessmentResult) {
		var that = this;
		if (!result.id) {
			result.id = this.newId();
		}
		
		var saveData = {
			id: result.id
			,client_id: this.clientId
			,data: this.getAssessmentResultForSave(result)
			,assessment: {
				id: result.assessment.id
				,uid: result.assessment.uid
				,version: result.assessment.version
			}
			,client_custom_field_1: this.clientCustomField1
		};
		
		return this._dac$http.compressedPost("assessments/results.json", saveData)
			.then((response) => {
				this._results[result.id] = result;
				//this._$localStorage.result = result;
				this.forceTestRetake = false;
				// turn off the test retake if we have saved the result
				return result;
			}, (error) => {
				console.error("save failed", error);
				throw new Error("Save results failed");
			});
	}

	private getAssessmentResultForSave(result:AssessmentResult) {
		 let transformed = {
            _id: result.id
            ,_assignmentItemId: result.assignmentItemId
			,_assessment: {
				_id: result.assessment.id
				,_uid: result.assessment.uid
				,_version: result.assessment.version
			}
            ,_scaleResults: []
			,_answers: []
			,_flaggedQuestions: result.flaggedQuestions.map(q => q.id)
        };
        let scaleResults = result.scaleResults.map(sr => {
			if (!(sr.scale && sr.range)) {
				console.error("ScaleResult missing scale or range.  Check assessment data.  Object: ", sr);
			}
            return {
                _scaleId: sr.scale ? sr.scale.id : null
                ,_score: sr.score
                ,_rangeId: sr.range ? sr.range.id : null
            }
        });
        transformed._scaleResults = scaleResults;

		let questionSets = result.assessment.getQuestionSetsForDisplay();
        questionSets.forEach(qs => qs.questions.forEach(q => {
            transformed._answers.push({_question_id: q.id, _answer:q.answerForSave, _score: q.getComputedScore()});
        }));
		return transformed;
	}
	
	private resolvePendingResults(resultID, serverResponse) {
		let data = serverResponse.json() as any;
		var result:AssessmentResult = this.createAssessmentResultFromJSON(data);

		// get it from the assessment if we didn't get one in our bound parameters
		// this only happens when a client first comes to an assessment and we don't know
		// their top result.
		if (!resultID) {
			resultID = result.id;
		}
		
		this._results[resultID] = result;
		if (!this._pendingResults[resultID]) {
			return;
		}
		
		this._pendingResults[resultID].forEach((defer) => defer.resolve(result));
		delete this._pendingResults[resultID];
	}
	
	private addPendingDefer(defer, resultID) {
		if (!this._pendingResults[resultID]) {
			this._pendingResults[resultID] = [];
		}
		this._pendingResults[resultID].push(defer);
	}
	
	getLatestClientAssessmentResult(clientId:string, assessmentUID:string) {
		var requestData = {
				clientID: clientId
				,assessmentUID: assessmentUID
				,resultID: null
			};
		return new Promise((resolve, reject) => {
			this._dac$http.get("assessments/results.json", requestData)
				.then((serverResponse) => {
					let data = serverResponse.json() as any;
					var result:AssessmentResult = this.createAssessmentResultFromJSON(data);
					this._results[result.id] = result;
					resolve(result);
					return result;
				}, (error) => {
					reject(error);
					return error;
				});
		});
	}
	
	getAssessmentResult(clientId, resultID?:string) : Promise<AssessmentResult> {
		var requestData:any = {
					clientID: clientId,
		};
		if (resultID) {
			requestData.resultID = resultID;
		}
		else {
			requestData.assessmentUID = this.uid;
		}

		console.log("attempting to grab results");
		return this._dac$http.get("assessments/results.json", requestData)
			.then((serverResponse) => {
				let data = serverResponse.json() as any;
					var result:AssessmentResult = this.createAssessmentResultFromJSON(data);
					return result;
			});
	// 	let promise = new Promise((resolve, reject) => {

	// 		this._dac$http.get("assessments/results.json", requestData)
	// 		.then((serverResponse) => {
	// 			let data = serverResponse.json() as any;
	// 				var result:AssessmentResult = this.createAssessmentResultFromJSON(data);

	// 				// get it from the assessment if we didn't get one in our bound parameters
	// 				// this only happens when a client first comes to an assessment and we don't know
	// 				// their top result.
	// 				if (!resultID) {
	// 					resultID = result.id;
	// 				}
	// 		}, (error) => {
	// 			reject(error);
	// 			return error;
	// 		});

	// 		console.log("promise is: ", promise);
	// 		if (resultID && this._results[resultID]) {
	// 			resolve(this._results[resultID]);
	// 		}
	// 		else if (resultID && this._pendingResults[resultID]) {
	// 			this.addPendingDefer(promise, resultID);
	// 		}
	// 		else {
	// 			var requestData = {
	// 				clientID: clientId
	// 				,assessmentUID: this.uid
	// 				,resultID: null
	// 			};
				
	// 			let errorCallback = (error) => {
	// 				reject(error);
	// 				return error;
	// 			};
			
	// 			if (resultID) {
	// 				requestData.resultID = resultID;
	// 				this.addPendingDefer(promise, resultID);			
	// 			}
	// 			this._dac$http.get("assessments/results.json", requestData)
	// 					.then(this.resolvePendingResults.bind(this, resultID), errorCallback);
	// 		}
	// 	});
	// 	return promise;
	// }
	
	// clearResultCache() {
    // // does nothing as we have no caching layer at this point
    // // TODO: stephen look at removing this function.
	}
}


// WEBPACK FOOTER //
// ./src/app/shared/services/assessment.service.ts